import * as THREE from "three";
import {
    cloneAxes,
    IDENTITY_AXES,
    isCardinalAxes,
    normalizeAxesWithFaceFlip,
    normalizeDeg,
    quantizeAxesTo90Degrees,
} from "./orientationAxes";

export const DEFAULT_LOCAL_ROTATION_ANGLES = Object.freeze({x: 0, y: 0, z: 0});

const LOCAL_AXIS_KEYS = ["x", "y", "z"];

/** Угол только на одной локальной оси (остальные 0). */
export const anglesOnSingleAxis = (axisKey, degrees) => {
    const key = LOCAL_AXIS_KEYS.includes(axisKey) ? axisKey : "z";
    return {
        x: key === "x" ? normalizeDeg(degrees) : 0,
        y: key === "y" ? normalizeDeg(degrees) : 0,
        z: key === "z" ? normalizeDeg(degrees) : 0,
    };
};

/** Оставить ненулевой угол только на выбранной оси. */
export const enforceSingleAxisAngles = (angles, axisKey) => {
    const key = LOCAL_AXIS_KEYS.includes(axisKey) ? axisKey : "z";
    const value = normalizeDeg(Number(angles?.[key]) || 0);
    return anglesOnSingleAxis(key, value);
};

/** Есть ли заметный наклон относительно reference (не кратный 90°). */
export const panelHasObliqueTilt = (referenceAxes, anglesDeg) => {
    const angles = normalizeLocalRotationAngles(anglesDeg);
    for (const key of LOCAL_AXIS_KEYS) {
        const deg = Math.abs(normalizeDeg(angles[key]));
        if (deg < 0.05) continue;
        const mod90 = Math.abs(deg % 90);
        if (mod90 > 0.05 && mod90 < 90 - 0.05) return true;
    }
    return false;
};

export const composePanelAxes = (referenceAxes, anglesDeg, activeAxisKey = null) => {
    const angles = activeAxisKey
        ? enforceSingleAxisAngles(anglesDeg, activeAxisKey)
        : normalizeLocalRotationAngles(anglesDeg);
    return composeAxesFromLocalAngles(referenceAxes, angles);
};

const LOCAL_AXIS_ORDER = ["x", "y", "z"];

export const normalizeLocalRotationAngles = (angles) => ({
    x: normalizeDeg(Number(angles?.x) || 0),
    y: normalizeDeg(Number(angles?.y) || 0),
    z: normalizeDeg(Number(angles?.z) || 0),
});

/** Поворот базиса вокруг текущей локальной оси (направление axes[axisKey] в мире). */
export const rotateAxesAroundLocalAxis = (axes, axisKey, degrees) => {
    const key = axisKey;
    if (!LOCAL_AXIS_ORDER.includes(key)) return cloneAxes(axes);
    const radians = THREE.MathUtils.degToRad(Number(degrees) || 0);
    if (!Number.isFinite(radians) || Math.abs(radians) < 1e-12) {
        return cloneAxes(axes);
    }
    const current = cloneAxes(axes);
    const axisVec = new THREE.Vector3(...(current[key] || IDENTITY_AXES[key])).normalize();
    if (axisVec.lengthSq() < 1e-12) return current;
    const delta = new THREE.Quaternion().setFromAxisAngle(axisVec, radians);
    const rotate = (tuple) =>
        new THREE.Vector3(...tuple).applyQuaternion(delta).normalize();

    return {
        x: [rotate(current.x).x, rotate(current.x).y, rotate(current.x).z],
        y: [rotate(current.y).x, rotate(current.y).y, rotate(current.y).z],
        z: [rotate(current.z).x, rotate(current.z).y, rotate(current.z).z],
    };
};

/**
 * Направление оси вращения для угла axisKey (порядок X→Y→Z до шага axisKey).
 * Вектор в том же пространстве, что referenceAxes / orient.
 */
export const getLocalRotationAxisVector = (referenceAxes, anglesDeg, axisKey) => {
    if (!LOCAL_AXIS_ORDER.includes(axisKey)) {
        return [...(IDENTITY_AXES[axisKey] || [0, 0, 1])];
    }
    let current = cloneAxes(referenceAxes || IDENTITY_AXES);
    const angles = normalizeLocalRotationAngles(anglesDeg);
    for (const key of LOCAL_AXIS_ORDER) {
        if (key === axisKey) {
            const tuple = current[key] || IDENTITY_AXES[key];
            return [
                Number(tuple[0]) || 0,
                Number(tuple[1]) || 0,
                Number(tuple[2]) || 0,
            ];
        }
        const deg = Number(angles[key]) || 0;
        if (Math.abs(deg) >= 1e-9) {
            current = rotateAxesAroundLocalAxis(current, key, deg);
        }
    }
    const tuple = current[axisKey] || IDENTITY_AXES[axisKey];
    return [
        Number(tuple[0]) || 0,
        Number(tuple[1]) || 0,
        Number(tuple[2]) || 0,
    ];
};

/** Собрать ориентацию: последовательно X → Y → Z вокруг локальных осей от reference. */
export const composeAxesFromLocalAngles = (referenceAxes, anglesDeg) => {
    let current = cloneAxes(referenceAxes || IDENTITY_AXES);
    for (const key of LOCAL_AXIS_ORDER) {
        const deg = Number(anglesDeg?.[key]) || 0;
        if (Math.abs(deg) < 1e-9) continue;
        current = rotateAxesAroundLocalAxis(current, key, deg);
    }
    return current;
};

const axesDistance = (a, b) => {
    let sum = 0;
    for (const key of LOCAL_AXIS_ORDER) {
        const ta = a?.[key] || IDENTITY_AXES[key];
        const tb = b?.[key] || IDENTITY_AXES[key];
        for (let i = 0; i < 3; i += 1) {
            const d = (Number(ta[i]) || 0) - (Number(tb[i]) || 0);
            sum += d * d;
        }
    }
    return sum;
};

/** Углы (градусы) вокруг локальных X,Y,Z от reference до current (порядок X→Y→Z). */
export const extractLocalAnglesDeg = (referenceAxes, currentAxes) => {
    const result = {x: 0, y: 0, z: 0};
    for (const key of LOCAL_AXIS_ORDER) {
        let bestDeg = 0;
        let bestErr = Infinity;
        const partial = {...result};
        for (let deg = -180; deg <= 180; deg += 0.5) {
            partial[key] = deg;
            const trial = composeAxesFromLocalAngles(referenceAxes, partial);
            const err = axesDistance(trial, currentAxes);
            if (err < bestErr) {
                bestErr = err;
                bestDeg = deg;
            }
        }
        result[key] = Math.round(bestDeg * 1000) / 1000;
    }
    return normalizeLocalRotationAngles(result);
};

/** Ось с ненулевым углом наклона; null — можно выбрать любую ось. */
export const lockedPanelRotationAxisKey = (angles) => {
    const normalized = normalizeLocalRotationAngles(angles);
    for (const key of ["x", "y", "z"]) {
        if (Math.abs(Number(normalized[key]) || 0) > 0.05) {
            return key;
        }
    }
    return null;
};

export const dominantRotationAxisKey = (angles) => {
    const keys = ["x", "y", "z"];
    return keys.reduce(
        (best, key) => (
            Math.abs(Number(angles?.[key]) || 0) > Math.abs(Number(angles?.[best]) || 0)
                ? key
                : best
        ),
        "z",
    );
};

/**
 * Поза панели из сохранённых осей (+ опционально reference/angles из localStorage).
 */
export const resolvePanelPoseFromAxes = ({
    axes: rawAxes,
    faceFlip: rawFaceFlip = false,
    referenceAxes: savedReferenceAxes = null,
    localRotationAngles: savedLocalAngles = null,
}) => {
    const normalized = normalizeAxesWithFaceFlip(rawAxes || IDENTITY_AXES, rawFaceFlip);
    let axes = normalized.axes;
    let faceFlip = normalized.faceFlip;

    if (savedReferenceAxes && savedLocalAngles) {
        const referenceAxes = cloneAxes(savedReferenceAxes);
        const localRotationAngles = enforceSingleAxisAngles(
            normalizeLocalRotationAngles(savedLocalAngles),
            dominantRotationAxisKey(savedLocalAngles),
        );
        if (panelHasObliqueTilt(referenceAxes, localRotationAngles) || !isCardinalAxes(axes)) {
            const composed = composePanelAxes(referenceAxes, localRotationAngles);
            const composedNorm = normalizeAxesWithFaceFlip(composed, faceFlip);
            return {
                axes: composedNorm.axes,
                faceFlip: composedNorm.faceFlip,
                referenceAxes,
                localRotationAngles,
            };
        }
    }

    if (!isCardinalAxes(axes)) {
        const referenceAxes = cloneAxes(quantizeAxesTo90Degrees(axes, faceFlip).axes);
        const extracted = extractLocalAnglesDeg(referenceAxes, axes);
        const activeKey = dominantRotationAxisKey(extracted);
        const localRotationAngles = enforceSingleAxisAngles(extracted, activeKey);
        const composed = composePanelAxes(referenceAxes, localRotationAngles, activeKey);
        const composedNorm = normalizeAxesWithFaceFlip(composed, faceFlip);
        return {
            axes: composedNorm.axes,
            faceFlip: composedNorm.faceFlip,
            referenceAxes,
            localRotationAngles,
        };
    }

    const quant = quantizeAxesTo90Degrees(axes, faceFlip);
    return {
        axes: quant.axes,
        faceFlip: quant.faceFlip,
        referenceAxes: quant.referenceAxes,
        localRotationAngles: quant.localRotationAngles,
    };
};

export const resolvePanelRotationState = (inst) => {
    const axes = inst?.axes || IDENTITY_AXES;
    const referenceAxes = inst?.referenceAxes
        ? cloneAxes(inst.referenceAxes)
        : cloneAxes(axes);
    const localRotationAngles = inst?.localRotationAngles
        ? normalizeLocalRotationAngles(inst.localRotationAngles)
        : extractLocalAnglesDeg(referenceAxes, axes);
    return {referenceAxes, localRotationAngles};
};

export const withPanelRotationDefaults = (inst) => {
    if (!inst || inst.referenceAxes) return inst;
    const axes = inst.axes || IDENTITY_AXES;
    return {
        ...inst,
        referenceAxes: cloneAxes(axes),
        localRotationAngles: normalizeLocalRotationAngles(
            inst.localRotationAngles ?? DEFAULT_LOCAL_ROTATION_ANGLES,
        ),
    };
};
